import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./AssessmentPage.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faDollarSign,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  evaluateAnswers,
  evaluateAnswersSC,
  evaluateFlagsForQuestion,
  getTranslationForStatus,
} from "../evaluationHelper";
import axios from "axios";

const config = require("../../config.js");

const translations = {
  english: {
    "Substantial Contribution":
      "Substantial Contribution to Climate Change Mitigation",
    "DNSH - Adaptation": "DNSH: Climate Change Adaptation",
    "DNSH - Biodiversity": "DNSH: Biodiversity",
    "DNSH - Water": "DNSH: Water and Marine Protection",
    "DNSH - CE": "DNSH: Circular Economy",
    "DNSH - Pollution": "DNSH: Pollution Prevention",
    success:
      "Your answers are submitted. Please enter the assessment section to review or modify this assessment. If you have completed all activities and the minimum social safeguards for this financial year, go to the Dashboard section to see the evaluation. Click on create report to create a report for submission to expert check.",
    cancel: "Cancel",
    previous: "Previous",
    submit: "Submit",
    save: "Save",
    next: "Next",
    home: "Back to Home",
    thirdHome: "Back to Reports",
    expertHome: "Back to Reports",
    selectfile: "Select file",
  },
  german: {
    "Substantial Contribution": "Wesentlicher Beitrag zum Klimaschutz",
    "DNSH - Adaptation": "DNSH: Anpassung an den Klimawandel",
    "DNSH - Biodiversity": "DNSH: Biodiversität",
    "DNSH - Water": "DNSH: Wasser- und Meeresschutz",
    "DNSH - CE": "DNSH: Kreislaufwirtschaft",
    "DNSH - Pollution": "DNSH: Vermeidung von Umweltverschmutzung",
    success:
      "Die Antworten wurden eingereicht. Bitte gehen Sie in den Bewertungsbereich, um diese Bewertung zu überprüfen oder zu ändern. Wenn Sie alle Aktivitäten und die Mindestanforderungen an den sozialen Schutz für dieses Haushaltsjahr erfüllt haben, gehen Sie zum Abschnitt Dashboard, um die Bewertung einzusehen. Klicken Sie auf Bericht erstellen, um einen Bericht zur Vorlage bei der Expertenprüfung zu erstellen.",
    cancel: "Abbrechen",
    previous: "Zurück",
    submit: "Absenden",
    next: "Weiter",
    home: "Zur Startseite",
    thirdHome: "Zurück zu Reports",
    expertHome: "Zurück zu Reports",
    selectfile: "Datei auswählen",
    save: "Speichern",
  },
  italian: {
    "Substantial Contribution":
      "Contributo Sostanziale alla Mitigazione dei Cambiamenti Climatici",
    "DNSH - Adaptation": "DNSH: Adattamento ai Cambiamenti Climatici",
    "DNSH - Biodiversity": "DNSH: Biodiversità",
    "DNSH - Water": "DNSH: Protezione dell'Acqua e del Mare",
    "DNSH - CE": "DNSH: Economia Circolare",
    "DNSH - Pollution": "DNSH: Prevenzione dell'Inquinamento",
    success: "Le tue risposte sono state inviate",
    cancel: "Annulla",
    previous: "Precedente",
    submit: "Invia",
    next: "Successivo",
    home: "Indietro a Home",
    thirdHome: "Indietro a Reports",
    expertHome: "Indietro a Reports",
    selectfile: "Seleziona file",
    save: "Salva",
  },
  czech: {
    "Substantial Contribution":
      "Podstatný Příspěvek k Omezování Klimatických Změn",
    "DNSH - Adaptation": "DNSH: Přizpůsobení se Klimatickým Změnám",
    "DNSH - Biodiversity": "DNSH: Biodiverzita",
    "DNSH - Water": "DNSH: Ochrana Vody a Moře",
    "DNSH - CE": "DNSH: Cirkulární Ekonomika",
    "DNSH - Pollution": "DNSH: Prevence Znečištění",
    success: "Vaše odpovědi byly odeslány",
    cancel: "Zrušit",
    previous: "Předchozí",
    submit: "Odeslat",
    next: "Další",
    home: "Zpět Domů",
    thirdHome: "Zpět Reports",
    expertHome: "Zpět Reports",
    selectfile: "Vyberte soubor",
    save: "Uložit",
  },
};

let examDescription = "";
let isLastQuestion = false;

function AssessmentPage() {
  const { state } = useLocation();
  const {
    examName,
    examCategory,
    isMSS,
    assessmentId,
    edit,
    resultId,
    start,
    disabled,
  } = state || {};
  const [result, setResult] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const navigate = useNavigate();
  const [currentQuestionIDs, setCurrentQuestionIDs] = useState([]);
  const [questionHistory, setQuestionHistory] = useState([]);
  const [savedOptions, setSavedOptions] = useState([]);
  const [allCurrentQuestions, setAllCurrentQuestions] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem("language") || "english"
  );
  const [savedPreviousQuestions, setSavedPreviousQuestions] = useState([]);
  const [users, setUsers] = useState([]);
  const [reportYears, setReportYears] = useState([]);
  var files = [];
  const [filesUploadingStatus, setFilesUploadingStatus] = useState([]);
  var filesState = [];
  const isMSS2 = isMSS === "true";

  const saveResults = async () => {
    if (!disabled) {
      const userEmail = localStorage.getItem("email");
      const user = localStorage.getItem("user");
      try {
        // Accumulate all question IDs in allCurrentQuestions
        const questionIDs = allCurrentQuestions.map(
          (question) => question.questionID
        );
        // Filter out duplicate question IDs
        const uniqueQuestionsMap = new Map();
        questions.forEach((question) => {
          if (!uniqueQuestionsMap.has(question.questionID)) {
            uniqueQuestionsMap.set(question.questionID, question);
          }
        });
        const uniqueQuestions = Array.from(uniqueQuestionsMap.values());
        const uniqueQuestionIDs = Array.from(uniqueQuestionsMap.keys());

        const optionsWithFlagMap = new Map();

        allCurrentQuestions.forEach((question) => {
          // Initialize an empty array to hold flags for this question
          let flags = [];

          // Check if optionsWithFlag exists and is an array before processing
          if (
            question.optionsWithFlag &&
            Array.isArray(question.optionsWithFlag)
          ) {
            // Process each option for the current question
            question.optionsWithFlag.forEach((option) => {
              // Ensure option and its properties are not null or undefined
              if (
                option &&
                option.value !== undefined &&
                option.flag !== undefined &&
                answers[question.questionID] !== undefined
              ) {
                if (question.questionID) {
                  const currentAnswer = answers[question.questionID];

                  // If the answer is a string, check directly
                  if (
                    typeof currentAnswer === "string" &&
                    currentAnswer === option.value
                  ) {
                    flags.push(option.flag);
                  }

                  // If the answer is an array of objects, check each object's value0
                  else if (Array.isArray(currentAnswer)) {
                    currentAnswer.forEach((answerObj) => {
                      const obj = JSON.parse(answerObj);
                      if (obj?.value0 === option.value) {
                        flags.push(option.flag);
                      }
                    });
                  }
                }
              }
            });
          }

          // Store the array of flags in the Map
          if (flags.length > 0) {
            optionsWithFlagMap.set(question.questionID, flags);
          }
        });

        // Loop through each unique question and format the answers
        const formattedAnswers = uniqueQuestions.map((question) => {
          const currentAnswer = answers[question.questionID];
          const questionCategory = question.questionCategory;
          const questionType = question.questionType;
          const requireForEvaluation = question.requireForEvaluation;
          const meetsCriteria = evaluateFlagsForQuestion(
            optionsWithFlagMap,
            question,
            currentAnswer
          );
          if(questionType === "File" && Array.isArray(currentAnswer))
            console.log("file", currentAnswer.join(","));
          // Format the answer appropriately
          if (Array.isArray(currentAnswer)) {
            return {
              questionID: question.questionID,
              questionCategory,
              questionType,
              requireForEvaluation,
              meetsCriteria,
              answer:
                questionType === "File"
                  ? currentAnswer.join(",")
                  : currentAnswer.join(";"),
            };
          } else {
            return {
              questionID: question.questionID,
              questionCategory,
              questionType,
              requireForEvaluation,
              meetsCriteria,
              answer: currentAnswer || "",
            };
          }
        });

        isLastQuestion = currentQuestions.some(
          (question) => question.nextQuestions === "end"
        );

        const copyLast = isLastQuestion;

        const formData = new FormData();
        formData.append("assessmentId", assessmentId);
        formData.append("examName", examName);
        formData.append("examCategory", examCategory);
        formData.append("examDescription", examDescription);
        formData.append("isMSS", isMSS);
        formData.append("userEmail", userEmail);
        formData.append("answers", JSON.stringify(formattedAnswers));
        formData.append("questionIDs", uniqueQuestionIDs);
        formData.append("end", isLastQuestion || false);
        files.forEach((file) => formData.append("files", file));

        const url = edit
          ? "/api/results/editresults/" + resultId
          : "/api/results/submitresults";

        // Send the data to the backend
        const response = await axios.post(
          config.env.API_BASE_URL + url,
          formData,
          {
            headers: {
              "content-type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          const res = response.data;
          if (start) {
            navigate("/take-assessment", {
              state: {
                examName: res.examName,
                examCategory: res.examCategory,
                isMSS: res.isMSS,
                assessmentId: res.assessmentId,
                edit: true,
                resultId: res.id,
                start: false,
              },
            });
          } else {
            if (copyLast) {
              alert(translations[currentLanguage].success);
              navigate("/landing");
            }
          }
        } else {
          const errorData = await response.json();
          console.error("Failed to save results", errorData);
          alert(`Failed to save results: ${errorData.message}`);
        }
      } catch (error) {
        console.error("Error saving results:", error);
      }
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      if (user) setUsers(user);
    };
    fetchUsers();
  }, [filesUploadingStatus]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await fetch(
          config.env.API_BASE_URL + `/api/assessments/${examName}/questions`,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          setQuestions(data);
          if (data.length > 0) {
            setCurrentQuestionIDs([data[0].questionID]);
            setQuestionHistory([[data[0].questionID]]);
            setAllCurrentQuestions([data[0]]);

            if (start) saveResults();
          }

          if (edit) {
            const response2 = await fetch(
              config.env.API_BASE_URL + `/api/results/user/${resultId}`,
              {
                headers: {
                  Authorization: "Bearer " + localStorage.getItem("token"),
                },
              }
            );
            if (response2.ok) {
              const data2 = await response2.json();
              var newAnswers = [];
              data2.answers.forEach((element) => {
                newAnswers[parseInt(element.questionID)] = element.answer;
                if (element.questionType === "File" && element.answer) {
                  element.answer.forEach((subElement) => {
                    filesUploadingStatus.push({
                      questionID: element.questionID,
                      uploaded: true,
                      uploading: false,
                      name: subElement,
                    });
                    setFilesUploadingStatus((oldUpdate) => [
                      ...filesUploadingStatus,
                    ]);
                  });
                }
              });
              setAnswers((oldAnswers) => [...newAnswers]);

              setResult(data2);
              if (data2.answers) populateQuestions(data, data2.answers);
            } else {
              console.error("Failed to fetch questions");
            }
          }
        } else {
          console.error("Failed to fetch questions");
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, [examName, resultId]);

  useEffect(() => {
    const fetchYears = async () => {
      const all = edit ? 1 : 0;

      const response = await fetch(
        config.env.API_BASE_URL + `/api/reports/years?result=${resultId}&all=${all}`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setReportYears(data);
      }
    };

    fetchYears();
  }, []);

  const populateQuestions = (questions, res) => {
    const arrayQuestionTypes = ["MCQ", "MCQ-AfterMultiple", "examDescription"];
    const multipleQuestionTypes = ["Multiple Select", "Radio Buttons"];
    const answs = [];
    res.forEach((element) => {
      const question = questions.find(
        (question) => question.questionID == element.questionID
      );

      if (!question) return;
      answs[element.questionID] = element.answer;

      if (arrayQuestionTypes.includes(question.questionType))
        if (Array.isArray(element.answer) && element.answer.length > 0)
          answs[element.questionID] = element.answer[0];
      if (multipleQuestionTypes.includes(question.questionType)) {
        if (Array.isArray(element.answer) && element.answer.length > 0) {
          answs[element.questionID] = element.answer.map((e) => {
            return e;
            /*const vals = e2.split(",");
            return {
              value0: vals[0],
              value1: vals.length > 1 ? vals[1] : undefined,
            };*/
          });
        }
      }
    });
    setAnswers(answs);
  };

  const handleNextQuestion = () => {
    const newQuestionIDs = [];
    let addHello = false;
    let savedque = [];
    let shouldProceed = true;
    const shownAlerts = new Set();
    const shownToasts = new Set();
    const shownNotifications = new Set();

    currentQuestionIDs.forEach((id) => {
      const currentQuestion = questions.find((q) => q.questionID === id);
      const answer = answers[currentQuestion?.questionID] || "";

      if (currentQuestion) {
        const shouldAlert =
          [
            "MCQ",
            "MCQ-AfterMultiple",
            "Multiple Select",
            "Radio Buttons",
            "Short",
            "Long Text",
            "Numerical Value",
            "examDescription",
            "Year",
            "File",
          ].includes(currentQuestion.questionType) &&
          currentQuestion.alertText &&
          ((currentQuestion.questionType === "MCQ" && !answer) ||
            (currentQuestion.questionType === "MCQ-AfterMultiple" && !answer) ||
            (currentQuestion.questionType === "Multiple Select" &&
              (!answer || answer.length === 0)) ||
            (currentQuestion.questionType === "Radio Buttons" && !answer) ||
            (currentQuestion.questionType === "Short" && !answer) ||
            (currentQuestion.questionType === "Long Text" && !answer) ||
            (currentQuestion.questionType === "Numerical Value" && !answer) ||
            (currentQuestion.questionType === "examDescription" && !answer) ||
            (currentQuestion.questionType === "Year" && !answer) ||
            (currentQuestion.questionType === "File" && !answer));

        const shouldNotify =
          [
            "Multiple Select",
            "Radio Buttons",
            "Short",
            "Long Text",
            "Numerical Value",
            "examDescription",
            "Year",
          ].includes(currentQuestion.questionType) &&
          currentQuestion.notifytext &&
          // (currentQuestion.questionType === 'MCQ' && answer) ||
          ((currentQuestion.questionType === "Multiple Select" && answer) ||
            (currentQuestion.questionType === "Radio Buttons" && answer) ||
            (currentQuestion.questionType === "Short" && answer) ||
            (currentQuestion.questionType === "Long Text" && answer) ||
            (currentQuestion.questionType === "Numerical Value" && answer) ||
            (currentQuestion.questionType === "examDescription" && answer) ||
            (currentQuestion.questionType === "Year" && answer));

        const shouldNotifyNot =
          [
            "Multiple Select",
            "Radio Buttons",
            "Short",
            "Long Text",
            "Numerical Value",
            "examDescription",
            "Year",
          ].includes(currentQuestion.questionType) &&
          currentQuestion.notifynottext &&
          // (currentQuestion.questionType === 'MCQ' && !answer) ||
          ((currentQuestion.questionType === "Multiple Select" && !answer) ||
            (currentQuestion.questionType === "Radio Buttons" && !answer) ||
            (currentQuestion.questionType === "Short" && !answer) ||
            (currentQuestion.questionType === "Long Text" && !answer) ||
            (currentQuestion.questionType === "Numerical Value" && !answer) ||
            (currentQuestion.questionType === "examDescription" && !answer) ||
            (currentQuestion.questionType === "Year" && !answer));

        if (shouldAlert && !shownAlerts.has(currentQuestion.questionID)) {
          alert(currentQuestion.alertText);
          shouldProceed = false;
          shownAlerts.add(currentQuestion.questionID);
        }

        if (currentQuestion.nextQuestions) {
          const nextQuestionsArray = currentQuestion.nextQuestions
            .split(",")
            .map((q) => q.trim());
          const optionQuestionTypes = [
            "MCQ",
            //"MCQ-AfterMultiple",
            "Multiple Select",
            "Radio Buttons",
          ];

          if (
            optionQuestionTypes.includes(currentQuestion.questionType) &&
            Array.isArray(currentQuestion.options) &&
            currentQuestion.options.length > 0 &&
            answers[currentQuestion.questionID] !== undefined
          ) {
            const currentAnswers = Array.isArray(
              answers[currentQuestion.questionID]
            )
              ? answers[currentQuestion.questionID]
              : [answers[currentQuestion.questionID]]; // Ensure it's an array

            let currentAnswerIndexes = currentAnswers
              .map((currentAnswer) => {
                return currentAnswer && currentAnswer.value0
                  ? currentQuestion.options.indexOf(currentAnswer.value0)
                  : currentQuestion.options.indexOf(currentAnswer);
              })
              .filter((index) => index !== -1); // Filter out invalid indexes

            // Proceed only if valid answer indexes were found
            if (currentAnswerIndexes.length > 0) {
              const combinedNextQuestions = currentAnswerIndexes.flatMap(
                (index) => {
                  if (nextQuestionsArray[index]) {
                    return nextQuestionsArray[index]
                      .split("+")
                      .map((q) => q.trim());
                  } else {
                    return nextQuestionsArray.flatMap((nextQuestions) =>
                      nextQuestions.split("+").map((q) => q.trim())
                    );
                  }
                }
              );

              newQuestionIDs.push(...combinedNextQuestions);
            } else {
              // Split nextQuestionsArray by '+' if it exists
              const splitNextQuestions = nextQuestionsArray.flatMap(
                (nextQuestions) => nextQuestions.split("+").map((q) => q.trim())
              );

              newQuestionIDs.push(...splitNextQuestions);
            }
          } else if (
            currentQuestion.questionType === "MCQ-AfterMultiple" &&
            Array.isArray(currentQuestion.options) &&
            currentQuestion.options.length > 0 &&
            answers[currentQuestion.questionID] !== undefined
          ) {
            const currentAnswers = Array.isArray(
              answers[currentQuestion.questionID]
            )
              ? answers[currentQuestion.questionID]
              : [answers[currentQuestion.questionID]]; // Ensure it's an array

            let currentAnswerIndexes = currentAnswers
              .map((currentAnswer) => {
                const res = currentQuestion.options.find((option) => {
                  if (option.replace(";", "") == currentAnswer) return option;
                  return null;
                });
                return currentAnswer && res
                  ? currentQuestion.options.indexOf(res)
                  : -1;
              })
              .filter((index) => index !== -1); // Filter out invalid indexes

            // Proceed only if valid answer indexes were found
            if (currentAnswerIndexes.length > 0) {
              const combinedNextQuestions = currentAnswerIndexes.flatMap(
                (index) => {
                  if (nextQuestionsArray[index]) {
                    return nextQuestionsArray[index]
                      .split("+")
                      .map((q) => q.trim());
                  } else {
                    return nextQuestionsArray.flatMap((nextQuestions) =>
                      nextQuestions.split("+").map((q) => q.trim())
                    );
                  }
                }
              );

              newQuestionIDs.push(...combinedNextQuestions);
            } else {
              // Split nextQuestionsArray by '+' if it exists
              const splitNextQuestions = nextQuestionsArray.flatMap(
                (nextQuestions) => nextQuestions.split("+").map((q) => q.trim())
              );

              newQuestionIDs.push(...splitNextQuestions);
            }
          } else if (
            currentQuestion.questionType === "Multiple Select" &&
            currentQuestion.nextQIfSkipped &&
            (!answers[currentQuestion.questionID] ||
              answers[currentQuestion.questionID].length === 0)
          ) {
            const nextQIfSkippedArray = currentQuestion.nextQIfSkipped
              .split(",")
              .map((q) => q.trim());
            // Split nextQuestionsArray by '+' if it exists
            const splitNextQuestions = nextQIfSkippedArray.flatMap(
              (nextQuestions) => nextQuestions.split("+").map((q) => q.trim())
            );

            newQuestionIDs.push(...splitNextQuestions);
          } else {
            // Split nextQuestionsArray by '+' if it exists
            const splitNextQuestions = nextQuestionsArray.flatMap(
              (nextQuestions) => nextQuestions.split("+").map((q) => q.trim())
            );

            newQuestionIDs.push(...splitNextQuestions);
          }
        }

        const prevQuestions = questions.filter((q) =>
          currentQuestionIDs.includes(q.questionID)
        );

        prevQuestions.forEach((prevQuestion) => {
          if (
            prevQuestion &&
            (prevQuestion.questionType === "Multiple Select" ||
              prevQuestion.questionType === "Radio Buttons")
          ) {
            addHello = true; // Set flag to true to add Hello to the next question
            const savedOptionsForCurrentQuestion =
              savedOptions[prevQuestion.questionID];

            if (
              savedOptionsForCurrentQuestion &&
              savedOptionsForCurrentQuestion.length > 0
            ) {
              // Concatenate the new mapped options into savedque
              savedque = savedque.concat(
                savedOptionsForCurrentQuestion.map(
                  (opt) => opt.value1 || opt.value0
                )
              );
            }
          }
        });
      }
    });

    if (shouldProceed) {
      // Only proceed if no alert was shown

      const nextQuestions = newQuestionIDs.map((id) =>
        questions.find((q) => q.questionID === id)
      );
      setAllCurrentQuestions((prev) => [
        ...prev,
        ...nextQuestions.filter(Boolean),
      ]);
      setCurrentQuestionIDs(newQuestionIDs);
      setQuestionHistory((prevHistory) => [...prevHistory, newQuestionIDs]);

      if (addHello && newQuestionIDs.length > 0) {
        const nextQuestionID = newQuestionIDs[0];
        const nextQuestionIndex = questions.findIndex(
          (q) => q.questionID === nextQuestionID
        );

        if (nextQuestionIndex !== -1) {
          const nextQuestion = questions[nextQuestionIndex];
          const savedPrevQuestion = savedPreviousQuestions.find(
            (q) => q.id === nextQuestionIndex
          );
          const saveThisIfUndefined = nextQuestion.question;
          if (savedPrevQuestion === undefined) {
            setSavedPreviousQuestions((prevQuestions) => [
              ...prevQuestions,
              { id: nextQuestionIndex, question: saveThisIfUndefined },
            ]);
          }
          const allValues = savedque.flatMap((item) =>
            item ? item.split("¦").map((s) => s.trim()) : null
          );
          const uniqueValues = Array.from(new Set(allValues));
          const combinedSavedque = uniqueValues
            .map((item) => `<li>${item}</li>`)
            .join(" ");
          if (savedPrevQuestion === undefined) {
            nextQuestion.question = `${nextQuestion.question} ${combinedSavedque}`;
          } else {
            nextQuestion.question = `${savedPrevQuestion.question} ${combinedSavedque}`;
          }
          setQuestions([...questions]);
        }
      }

      saveResults();
    }
  };

  const handlePreviousQuestion = () => {
    if (questionHistory.length > 1) {
      const newHistory = [...questionHistory];
      newHistory.pop();
      const previousQuestionIDs = newHistory[newHistory.length - 1];
      setCurrentQuestionIDs(previousQuestionIDs);
      setQuestionHistory(newHistory);
    }
  };

  const currentQuestions = questions
    .filter((q) => currentQuestionIDs.includes(q.questionID))
    .filter(
      (q, index, self) =>
        index === self.findIndex((t) => t.questionID === q.questionID)
    )
    .sort(
      (a, b) =>
        currentQuestionIDs.indexOf(a.questionID) -
        currentQuestionIDs.indexOf(b.questionID)
    ); // Sort to maintain the order in currentQuestionIDs

  if (!currentQuestions.length) {
    return <div>Loading...</div>;
  }

  const handleAnswerChange = (questionID, answer) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionID]: answer,
    }));
  };

  const handleFileChange = (e, questionID) => {
    filesState = filesUploadingStatus;

    if (e.target.files) {
      var names = [];

      for (var i = 0; i < e.target.files.length; i++) {
        files.push(e.target.files[i]);
        names.push(e.target.files[i].name);
        filesState.push({
          uploading: false,
          uploaded: false,
          name: e.target.files[i].name,
          questionID: questionID,
        });
      }

      setFilesUploadingStatus([...filesState]);
      if (files?.length > 0) {
        for (let indexS = 0; indexS < files.length; indexS++) {
          let index = indexS + filesState.length - files.length;
          const file = files[indexS];
          if (
            filesState[index]?.uploaded === false &&
            filesState[index]?.uploading === false &&
            files[indexS]?.name
          ) {
            filesState[index].uploading = true;
            const formData = new FormData();

            formData.append("files", file);

            axios
              .post(config.env.API_BASE_URL + "/api/results/files", formData, {
                headers: {
                  "content-type": "multipart/form-data",
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              })
              // eslint-disable-next-line no-loop-func
              .then((res) => {
                filesState[index] = {
                  uploaded: true,
                  uploading: false,
                  name: res.data.name,
                  questionID: questionID,
                };
                var uploaded = [filesState[index]];

                setFilesUploadingStatus(
                  JSON.parse(JSON.stringify([...filesState]))
                );
                let arrayOfName = "";
                arrayOfName = JSON.parse(JSON.stringify([...filesState]))
                  .filter((fileState) => fileState.uploaded && fileState.questionID == questionID)
                  .map((myFileState) => myFileState.name);

                setAnswers((prevAnswers) => ({
                  ...prevAnswers,
                  [questionID]: arrayOfName.join(","),
                }));
              });
          }
        }

        const inputElement = document.getElementById(`question-${questionID}`);
        if (inputElement) {
          inputElement.value = '';
        }
      }
    }
  };

  const handleNumericalAnswerChange = (questionID, answer) => {
    const numericAnswer = Number(answer);

    if (numericAnswer < 0) {
      return;
    }

    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionID]: answer,
    }));
  };

  const handleCalculationAnswerChange = (questionID, answer, index) => {
    setAnswers((prevAnswers) => {
      const prevAnswer = prevAnswers[questionID] || [];
      const updatedAnswer = [...prevAnswer];

      // Initialize previous indexes if they don't exist
      for (let i = 0; i <= index; i++) {
        if (!updatedAnswer[i]) {
          updatedAnswer[i] = "";
        }
      }
      updatedAnswer[index] = answer;
      return {
        ...prevAnswers,
        [questionID]: updatedAnswer,
      };
    });
  };

  const handleDescriptionChange = (questionID, answer) => {
    setAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionID]: answer,
    }));
    examDescription = answer;
  };

  const handleMultipleSelectChange = (
    questionID,
    optionValue0,
    optionValue1,
    isChecked
  ) => {
    setAnswers((prevAnswers) => {
      const prevAnswer = prevAnswers[questionID] || [];
      let updatedAnswer;
      if (isChecked) {
        updatedAnswer = [
          ...prevAnswer,
          JSON.stringify({ value0: optionValue0, value1: optionValue1 }),
        ];
      } else {
        updatedAnswer = prevAnswer
          .filter((ans) => {
            const parsed = JSON.parse(ans);
            return parsed.value0 !== optionValue0;
          })
          .map((ans) => JSON.stringify(ans));
      }

      return {
        ...prevAnswers,
        [questionID]: updatedAnswer,
      };
    });

    if (optionValue1) {
      setSavedOptions((prevSavedOptions) => {
        const prevOptions = prevSavedOptions[questionID] || [];
        let updatedOptions;
        if (isChecked) {
          updatedOptions = [
            ...prevOptions,
            { value0: optionValue0, value1: optionValue1 },
          ];
        } else {
          updatedOptions = prevOptions.filter(
            (opt) => opt.value0 !== optionValue0
          );
        }
        return {
          ...prevSavedOptions,
          [questionID]: updatedOptions,
        };
      });
    }
  };

  const handleRadioButtonChange = (
    questionID,
    optionValue0,
    optionValue1,
    isChecked
  ) => {
    setAnswers((prevAnswers) => {
      let updatedAnswer;

      if (isChecked) {
        updatedAnswer = [
          JSON.stringify({ value0: optionValue0, value1: optionValue1 }),
        ];
      }

      return {
        ...prevAnswers,
        [questionID]: updatedAnswer,
      };
    });
    if (optionValue1) {
      setSavedOptions((prevSavedOptions) => {
        let updatedOptions;
        if (isChecked) {
          updatedOptions = [{ value0: optionValue0, value1: optionValue1 }];
        }
        return {
          ...prevSavedOptions,
          [questionID]: updatedOptions,
        };
      });
    }
  };

  const renderQuestionInput = (question) => {
    const savedAnswer = answers[question.questionID];

    switch (question.questionType) {
      case "MCQ":
        return (
          <>
            {question.options
              .filter((option) => option)
              .map((option, index) => (
                <div
                  key={index}
                  className="fs-6 case-mcq"
                  style={disabled ? { pointerEvents: "none" } : {}}
                >
                  <input
                    type="radio"
                    className="m-1 form-check-input"
                    name={`question-${question.questionID}`}
                    value={option}
                    checked={savedAnswer === option}
                    onChange={() =>
                      handleAnswerChange(question.questionID, option)
                    }
                  />
                  {option}
                </div>
              ))}
          </>
        );
      case "MCQ-AfterMultiple":
        return (
          <>
            {question.options
              .filter((option) => option)
              .map((option) => option.replace(";", ""))
              .map((option, index) => (
                <div
                  key={index}
                  className="fs-6 case-mcq-multiple"
                  style={disabled ? { pointerEvents: "none" } : {}}
                >
                  <input
                    type="radio"
                    className="m-1 form-check-input"
                    name={`question-${question.questionID}`}
                    value={option}
                    checked={savedAnswer === option}
                    onChange={() =>
                      handleAnswerChange(question.questionID, option)
                    }
                  />
                  {option}
                </div>
              ))}
          </>
        );
      case "Radio Buttons":
        return (
          <>
            {question.options
              .filter((option) => option)
              .map((option, index) => {
                const optionValue = option.split("#");

                return (
                  <div
                    key={index}
                    className="fs-6 case-radio"
                    style={disabled ? { pointerEvents: "none" } : {}}
                  >
                    <input
                      type="radio"
                      className="m-1 form-check-input"
                      name={`question-${question.questionID}`}
                      value={optionValue[0]}
                      checked={
                        savedAnswer &&
                        savedAnswer.some((ans) => {
                          const a = JSON.parse(ans);
                          return a && a.value0 === optionValue[0];
                        })
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        handleRadioButtonChange(
                          question.questionID,
                          optionValue[0],
                          optionValue[1],
                          isChecked
                        );
                      }}
                    />
                    {optionValue[0]}
                  </div>
                );
              })}
          </>
        );
      case "Multiple Select":
        return (
          <>
            {question.options
              .filter((option) => option)
              .map((option, index) => {
                const optionValue = option.split("#");

                return (
                  <div
                    key={index}
                    className="fs-6"
                    style={disabled ? { pointerEvents: "none" } : {}}
                  >
                    <input
                      type="checkbox"
                      className="m-1 form-check-input"
                      name={`question-${question.questionID}`}
                      value={optionValue[0]}
                      checked={
                        savedAnswer &&
                        savedAnswer.some((ans) => {
                          const a = JSON.parse(ans);
                          return a && a.value0 === optionValue[0];
                        })
                      }
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        handleMultipleSelectChange(
                          question.questionID,
                          optionValue[0],
                          optionValue[1],
                          isChecked
                        );
                      }}
                    />
                    {optionValue[0]}
                  </div>
                );
              })}
          </>
        );

      case "Short":
        return (
          <textarea
            disabled={disabled}
            className="input-4 border-secondary text-secondary w-100"
            name={`question-${question.questionID}`}
            value={savedAnswer || ""}
            onChange={(e) =>
              handleAnswerChange(question.questionID, e.target.value)
            }
            min
          />
        );

      case "Long Text":
        return (
          <textarea
            disabled={disabled}
            className="input-4 border-secondary text-secondary w-100 min-h-250"
            //rows={10}
            name={`question-${question.questionID}`}
            value={savedAnswer || ""}
            onChange={(e) =>
              handleAnswerChange(question.questionID, e.target.value)
            }
          />
        );
      case "Numerical Value":
        return (
          <div
            className="numerical"
            style={disabled ? { pointerEvents: "none" } : {}}
          >
            <input
              type="number"
              className="input-4 border-secondary text-secondary w-100"
              name={`question-${question.questionID}`}
              value={savedAnswer || ""}
              onChange={(e) =>
                handleNumericalAnswerChange(question.questionID, e.target.value)
              }
            />
            <div className="euro-sign">{users.currency}</div>
          </div>
        );
      case "File":
        return (
          <div>
            <div className="input-group notext">
              <label
                style={disabled ? { pointerEvents: "none" } : {}}
                className="input-group-text"
                htmlFor={`question-${question.questionID}`}
              >
                {translations[currentLanguage].selectfile}
              </label>
              <span
                className="input-4 border-secondary text-secondary overflow-hidden"
                style={disabled ? { pointerEvents: "none" } : {}}
              ></span>
              <input
                style={disabled ? { pointerEvents: "none" } : {}}
                type="file"
                id={`question-${question.questionID}`}
                className="invisible"
                name={`question-${question.questionID}[]`}
                multiple
                onChange={(e) => handleFileChange(e, question.questionID)}
              />
            </div>

            {filesUploadingStatus.find(
              (myFilesStat) => myFilesStat.uploading === true
            ) ? (
              <div className="loader-container">
                <div className="loader"></div>
              </div>
            ) : null}

            <div style={{ marginTop: "12px", display: "grid", gap: "8px" }}>
              {filesUploadingStatus
                .filter(
                  (myFileStatus) =>
                    myFileStatus.questionID === question.questionID &&
                    myFileStatus.name !== ""
                )
                .map((file, index) => {
                  return (
                    <div
                      style={{
                        padding: "2px",
                        font: "14px",
                        display: "flex",
                      }}
                    >
                      <div style={disabled ? { color: "blue" } : {}}>
                        <a
                          href={config.env.STORAGE_URL + file?.name}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {file?.name}
                        </a>
                      </div>
                      <div
                        style={
                          disabled
                            ? { pointerEvents: "none", padding: "2px" }
                            : {
                                marginLeft: "16px",
                                cursor: "pointer",
                                padding: "2px",
                              }
                        }
                        onClick={async () => {
                          setFilesUploadingStatus([
                            ...JSON.parse(
                              JSON.stringify(
                                filesUploadingStatus.filter(
                                  (file) =>
                                    file.name &&
                                    file.name !==
                                      filesUploadingStatus.filter(
                                        (myFileStatus) =>
                                          myFileStatus.questionID ===
                                            question.questionID &&
                                          myFileStatus.name !== ""
                                      )[index].name
                                )
                              )
                            ),
                          ]);

                          const toDelete = filesUploadingStatus.filter(
                                  (myFileStatus) =>
                                    myFileStatus.questionID ===
                                      question.questionID &&
                                    myFileStatus.name !== ""
                                )[index].name;

                          const response = await fetch(
                            config.env.API_BASE_URL +
                              `/api/results/file?file=${toDelete}&resultId=${resultId}`,
                            {
                              method: "DELETE",
                              headers: {
                                Authorization:
                                  "Bearer " + localStorage.getItem("token"),
                              },
                            }
                          );

                          let arrayOfName = JSON.parse(
                            JSON.stringify(
                              filesUploadingStatus.filter(
                                (file) =>
                                  file.name &&
                                  file.name !== toDelete &&
                                  file.questionID === question.questionID 
                              )
                            )
                          ).map((myFileState) => myFileState.name);

                          setAnswers((prevAnswers) => ({
                            ...prevAnswers,
                            [question.questionID]: arrayOfName.join(","),
                          }));

                          // saveResults();
                        }}
                      >
                        {file.uploading ? (
                          <FontAwesomeIcon
                            color="gray"
                            icon={faX}
                          ></FontAwesomeIcon>
                        ) : null}
                        {file.uploaded ? (
                          <FontAwesomeIcon
                            color="red"
                            icon={faX}
                          ></FontAwesomeIcon>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        );
      case "Input Validation":
        return (
          <div
            className="numerical"
            style={disabled ? { pointerEvents: "none" } : {}}
          >
            <input
              type="number"
              className="input-4 border-secondary text-secondary w-100"
              name={`question-${question.questionID}`}
              value={savedAnswer || ""}
              onChange={(e) =>
                handleAnswerChange(question.questionID, e.target.value)
              }
            />
            {/* <FontAwesomeIcon
                            icon={faEuroSign}
                            className='euro-sign'
                        /> */}
          </div>
        );
      case "Calculation":
        const savedAnswersForIndex = answers[question.questionID] || [];

        return (
          <>
            {question.options
              .filter((option) => option)
              .map((option, index) => {
                return (
                  <div
                    key={index}
                    className="numerical-unit"
                    style={disabled ? { pointerEvents: "none" } : {}}
                  >
                    <input
                      type="number"
                      className="input-4 border-secondary text-secondary w-100"
                      name={`question-${question.questionID}`}
                      value={savedAnswersForIndex[index] || ""}
                      onChange={(e) =>
                        handleCalculationAnswerChange(
                          question.questionID,
                          e.target.value,
                          index
                        )
                      }
                    />
                    <span className="unit">{option}</span> {}
                  </div>
                );
              })}
          </>
        );
      case "examDescription":
        return (
          <textarea
            disabled={disabled}
            className="input-4 border-secondary text-secondary w-100"
            name="examDescription"
            value={savedAnswer || ""}
            onChange={(e) =>
              handleDescriptionChange(question.questionID, e.target.value)
            }
          />
        );
      case "Year":
        return (
          <div
            className="numerical"
            style={disabled ? { pointerEvents: "none" } : {}}
          >
            <select
              id={`year-select`}
              className="form-select select-year"
              name="selectYear"
              value={savedAnswer}
              onChange={(e) =>
                handleAnswerChange(question.questionID, e.target.value)
              }
            >
              <option value=""></option>
              {reportYears.map((year) => (
                <option value={year}>{year}</option>
              ))}
            </select>
          </div>
        );
      default:
        return null;
    }
  };

  const evalTranslations = {
    english: {
      substantialContributions: "Substantial Contributions – Evaluation",
      climateChangeMitigation: "Climate change mitigation:",
      doNoSignificantHarm: "Do No Significant Harm – Evaluation",
      adaptation: "Climate change adaptation:",
      water: "Sustainable use and protection of water and marine resources:",
      ce: "Transition to a circular economy:",
      pollution: "Pollution prevention and control:",
      biodiversity:
        "Protection and restoration of biodiversity and ecosystems:",
    },
    german: {
      substantialContributions: "Wesentlicher Beitrag – Bewertung",
      climateChangeMitigation: "Klimaschutz:",
      doNoSignificantHarm:
        "Vermeidung erheblicher Beeinträchtigungen – Bewertung",
      adaptation: "Anpassung an den Klimawandel:",
      water: "Nachhaltige Nutzung und Schutz von Wasser- und Meeresressourcen:",
      ce: "Übergang zu einer Kreislaufwirtschaft:",
      pollution: "Vermeidung und Verminderung der Umweltverschmutzung:",
      biodiversity:
        "Schutz und Wiederherstellung der Biodiversität und der Ökosysteme:",
    },
    czech: {
      substantialContributions: "Podstatné příspěvky – Hodnocení",
      climateChangeMitigation: "Zmírnění změny klimatu:",
      doNoSignificantHarm: "Nedělat žádnou významnou škodu – Hodnocení",
      adaptation: "Adaptace na změnu klimatu:",
      water: "Udržitelné využívání a ochrana vodních a mořských zdrojů:",
      ce: "Přechod na oběhové hospodářství:",
      pollution: "Prevence a kontrola znečištění:",
      biodiversity: "Ochrana a obnova biodiverzity a ekosystémů:",
    },
    italian: {
      substantialContributions: "Contributi Sostanziali – Valutazione",
      climateChangeMitigation: "Mitigazione dei Cambiamenti Climatici:",
      doNoSignificantHarm: "Non Arrecare un Danno Significativo – Valutazione",
      adaptation: "Adattamento ai Cambiamenti Climatici:",
      water: "Uso Sostenibile e Protezione delle Risorse Idriche e Marine:",
      ce: "Transizione verso un'Economia Circolare:",
      pollution: "Prevenzione e Controllo dell'Inquinamento:",
      biodiversity:
        "Protezione e Ripristino della Biodiversità e degli Ecosistemi:",
    },
  };

  isLastQuestion = currentQuestions.some(
    (question) => question.nextQuestions === "end"
  );

  const ineligibleAnswers =
    isLastQuestion && currentQuestions.some((question) => question.doNotSave);
  const excludedCategories = [
    "Turnover",
    "Capex",
    "OpEx",
    "Blank",
    "Fiscal Year",
  ];
  const currentCategory = currentQuestions[0]?.questionCategory;

  const changeLanguage = (language) => {
    if (currentLanguage !== language) {
      localStorage.setItem("language", language);
      setCurrentLanguage(language);
      window.location.reload(); // Force reload to apply language changes
    }
  };

  const translatedCategory =
    translations[currentLanguage][currentCategory] || currentCategory;

  // Accumulate all question IDs in allCurrentQuestions
  const questionIDs = allCurrentQuestions.map(
    (question) => question.questionID
  );
  // Filter out duplicate question IDs
  const uniqueQuestionsMap = new Map();
  allCurrentQuestions.forEach((question) => {
    if (!uniqueQuestionsMap.has(question.questionID)) {
      uniqueQuestionsMap.set(question.questionID, question);
    }
  });
  const uniqueQuestions = Array.from(uniqueQuestionsMap.values());
  const uniqueQuestionIDs = Array.from(uniqueQuestionsMap.keys());

  const optionsWithFlagMap = new Map();

  allCurrentQuestions.forEach((question) => {
    // Initialize an empty array to hold flags for this question
    let flags = [];

    // Check if optionsWithFlag exists and is an array before processing
    if (question.optionsWithFlag && Array.isArray(question.optionsWithFlag)) {
      // Process each option for the current question
      question.optionsWithFlag.forEach((option) => {
        // Ensure option and its properties are not null or undefined
        if (
          option &&
          option.value !== undefined &&
          option.flag !== undefined &&
          answers[question.questionID] !== undefined
        ) {
          if (question.questionID) {
            const currentAnswer = answers[question.questionID];

            if (
              question.questionType === "MCQ-AfterMultiple" &&
              typeof currentAnswer === "string" &&
              currentAnswer === option.value.replace(";", "")
            ) {
              flags.push(option.flag);
            }
            // If the answer is a string, check directly
            else if (
              typeof currentAnswer === "string" &&
              currentAnswer === option.value
            ) {
              flags.push(option.flag);
            }

            // If the answer is an array of objects, check each object's value0
            else if (Array.isArray(currentAnswer)) {
              currentAnswer.forEach((answerObj) => {
                const obj = JSON.parse(answerObj);
                if (obj?.value0 === option.value) {
                  flags.push(option.flag);
                }
              });
            }
          }
        }
      });
    }

    // Store the array of flags in the Map
    if (flags.length > 0) {
      optionsWithFlagMap.set(question.questionID, flags);
    }
  });

  // Loop through each unique question and format the answers
  const formattedAnswers = uniqueQuestions.map((question) => {
    const currentAnswer = answers[question.questionID];
    const questionCategory = question.questionCategory;
    const questionType = question.questionType;
    const requireForEvaluation = question.requireForEvaluation;
    const meetsCriteria = evaluateFlagsForQuestion(
      optionsWithFlagMap,
      question,
      currentAnswer
    );

    const jj = questionType === "File" ? "," : ";";

    // Format the answer appropriately
    if (Array.isArray(currentAnswer)) {
      return {
        questionID: question.questionID,
        questionCategory,
        requireForEvaluation,
        meetsCriteria,
        answer:
          currentAnswer.length > 0
            ? currentAnswer
                .map((b) => {
                  // Only include value1 if it is defined
                  let a = b;
                  try {
                    a = JSON.parse(b);
                  } catch (error) {}
                  const value1Part =
                    a.value1 !== undefined ? `,${a.value1}` : "";
                  return `${a.value0}${value1Part}`;
                })
                .join(jj)
            : "",
      };
    } else {
      return {
        questionID: question.questionID,
        questionCategory,
        questionType,
        requireForEvaluation,
        meetsCriteria,
        answer: currentAnswer || "",
      };
    }
  });

  const substantialStatus = evaluateAnswers(
    formattedAnswers,
    "Substantial Contribution"
  );
  // Adaptation is grey if filled out because we cannot automatically check text answers
  const adaptationStatus = evaluateAnswers(
    formattedAnswers,
    "DNSH - Adaptation",
    /* categoryIsAssessable= */ false
  );
  const waterStatus = evaluateAnswers(formattedAnswers, "DNSH - Water");
  const ceStatus = evaluateAnswers(formattedAnswers, "DNSH - Circular Economy");
  const pollutionStatus = evaluateAnswers(formattedAnswers, "DNSH - Pollution");
  const biodiversityStatus = evaluateAnswers(
    formattedAnswers,
    "DNSH - Biodiversity"
  );

  return (
    <div className="assessment-page container mt-5 py-5">
      <h4>{examName}</h4>
      {excludedCategories.includes(currentCategory) ? (
        ""
      ) : (
        <h4
          className="container bg-secondary text-white p-2"
          style={{ borderRadius: "10px 10px 0px 0px" }}
        >
          {translatedCategory}
        </h4>
      )}

      {currentQuestions.map((question) => (
        <div key={question.questionID} className="question text-start">
          <p
            className="mt-5"
            dangerouslySetInnerHTML={{ __html: question.question }}
          ></p>
          {renderQuestionInput(question)}
          <p
            className="mt-5"
            dangerouslySetInnerHTML={{ __html: question.disclaimer }}
          ></p>
          {/* <p className='mt-3'><i>{question.disclaimer}</i></p> */}
        </div>
      ))}

      {isLastQuestion && !isMSS2 && (
        <div className="asd" style={{ height: "400px" }}>
          <h5 className="text-start">
            {evalTranslations[currentLanguage].substantialContributions}
          </h5>
          <br />
          <div className="text-start">
            <strong>
              {evalTranslations[currentLanguage].climateChangeMitigation}
            </strong>{" "}
            {getTranslationForStatus(substantialStatus, currentLanguage)}
          </div>
          <br />
          <br />
          <h5 className="text-start">
            {evalTranslations[currentLanguage].doNoSignificantHarm}
          </h5>
          <br />
          <div className="text-start">
            <strong>{evalTranslations[currentLanguage].adaptation}</strong>{" "}
            {getTranslationForStatus(adaptationStatus, currentLanguage)}
          </div>
          <br />
          <div className="text-start">
            <strong>{evalTranslations[currentLanguage].water}</strong>{" "}
            {getTranslationForStatus(waterStatus, currentLanguage)}
          </div>
          <br />
          <div className="text-start">
            <strong>{evalTranslations[currentLanguage].ce}</strong>{" "}
            {getTranslationForStatus(ceStatus, currentLanguage)}
          </div>
          <br />
          <div className="text-start">
            <strong>{evalTranslations[currentLanguage].pollution}</strong>{" "}
            {getTranslationForStatus(pollutionStatus, currentLanguage)}
          </div>
          <br />
          <div className="text-start">
            <strong>{evalTranslations[currentLanguage].biodiversity}</strong>{" "}
            {getTranslationForStatus(biodiversityStatus, currentLanguage)}
          </div>
        </div>
      )}

      <div className="navigation-buttons d-flex justify-content-between mt-5">
        <button
          className="btn-cancel"
          onClick={() => {
            if (questionHistory.length <= 1) {
              navigate("/instructions", {
                state: { examName, examCategory, isMSS, assessmentId },
              });
            } else {
              handlePreviousQuestion();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          {questionHistory.length <= 1
            ? translations[currentLanguage].cancel
            : translations[currentLanguage].previous}
        </button>
        {ineligibleAnswers ? (
          <button
            className="btn-cancel"
            onClick={() => {
              navigate("/landing");
            }}
          >
            {translations[currentLanguage].home}
          </button>
        ) : isLastQuestion ? (
          !disabled ? (
            <button className="btn-cancel" onClick={saveResults}>
              {translations[currentLanguage].submit}
            </button>
          ) : (
            <button
              className="btn-cancel"
              onClick={() => {
                users.role === "third"
                  ? navigate("/reports/third/view")
                  : users.role === "expert"
                  ? navigate("/reports/expert/view")
                  : navigate("/landing");
              }}
            >
              {users.role === "third"
                ? translations[currentLanguage].thirdHome
                : users.role === "expert"
                ? translations[currentLanguage].expertHome
                : translations[currentLanguage].home}
            </button>
          )
        ) : (
          <>
            {!disabled && (
              <button className="btn-cancel" onClick={saveResults}>
                {translations[currentLanguage].save}
              </button>
            )}
            <button
              className="btn-cancel"
              onClick={() => {
                handleNextQuestion();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              {translations[currentLanguage].next}
            </button>
          </>
        )}
      </div>
      <ToastContainer />
    </div>
  );
}

export default AssessmentPage;

const Tooltip = ({ text, tooltipText }) => {
  const [hover, setHover] = useState(false);

  return (
    <span
      className="tooltip-container"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {text}
      {hover && <span className="tooltip">{tooltipText}</span>}
    </span>
  );
};
